/**
 * Mapping Session Context Module
 * Provides context for managing mapping session state
 *
 * @module MappingSessionContext
 */

import { createContext, PropsWithChildren, useContext } from 'react';
import { MappingSessionStore } from '../stores/MappingSessionStore';
import useClientInfo from '../hooks/useClientInfo';

/**
 * Context for sharing mapping session state
 * @type {React.Context<MappingSessionStore | null>}
 */
export const MappingSessionContext = createContext<MappingSessionStore | null>(null);

/**
 * Props for MappingSessionProvider component
 *
 * @interface MappingSessionProviderProps
 * @extends {PropsWithChildren}
 * @property {MappingSessionStore} [store] - Optional store instance to use
 */
interface MappingSessionProviderProps extends PropsWithChildren {
  store?: MappingSessionStore;
}

/**
 * Provider component for mapping session context
 * Creates or uses provided store instance
 *
 * @component
 * @param {MappingSessionProviderProps} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @param {MappingSessionStore} [props.store] - Optional store instance
 *
 * @example
 * // Basic usage
 * <MappingSessionProvider>
 *   <App />
 * </MappingSessionProvider>
 *
 * @example
 * // With custom store
 * const store = new MappingSessionStore();
 * <MappingSessionProvider store={store}>
 *   <App />
 * </MappingSessionProvider>
 */
export const MappingSessionProvider = ({ children, store }: MappingSessionProviderProps) => {
  const { userId, firstName, lastName } = useClientInfo();
  const mappingSessionStore =
    store || new MappingSessionStore({ userId, username: `${firstName} ${lastName}` });

  return (
    <MappingSessionContext.Provider value={mappingSessionStore}>
      {children}
    </MappingSessionContext.Provider>
  );
};

/**
 * Hook for accessing mapping session context
 * Must be used within MappingSessionProvider
 *
 * @hook
 * @returns {MappingSessionStore} Mapping session store instance
 * @throws {Error} When used outside of MappingSessionProvider
 *
 * @example
 * const store = useMappingSession();
 * const { isLoading, error } = store;
 */
export const useMappingSession = () => {
  const context = useContext(MappingSessionContext);
  if (!context) {
    throw new Error('useMappingSession must be used within a MappingSessionProvider');
  }
  return context;
};
